/**
 * ImportProgressChannel.js
 * --------------------------
 * Manages `import-progress.{progressId}` Echo channels (one subscription per
 * active import job). Mirrors the BoardChannel pattern: a private
 * `register*Listeners` function + `channelRegistry.subscribe`.
 */

import { channelRegistry } from "../Core/ChannelRegistry";
import { safe } from "../Core/safeListener";
import { useImportProgressChannel } from "../Composables/useImportProgressChannel";

const PROGRESS_UPDATED_EVENT = ".progress.updated";

/**
 * @param {import("laravel-echo").Channel} channel
 * @param {string} channelName
 */
function registerImportProgressChannelListeners(channel, channelName) {
    const actions = useImportProgressChannel();

    channel.subscribed(() => {
        console.log(
            `[ImportProgressChannel] subscription active: ${channelName}`,
        );
    });

    const events = {
        [PROGRESS_UPDATED_EVENT]: (data) => {
            const result = actions.onProgressUpdated(data);
            if (result?.done) {
                channelRegistry.unsubscribe(channelName);
            }
        },
    };

    Object.entries(events).forEach(([eventName, handler]) => {
        channel.listen(eventName, safe(eventName, handler));
    });

    channel.error((error) => {
        console.error("[ImportProgressChannel] channel error", error);
    });
}

/**
 * Subscribe to a single import-progress channel (idempotent per progress id).
 *
 * @param {string|number|null|undefined} progressId
 */
export function subscribeImportProgressChannel(progressId) {
    if (!progressId) {
        console.warn(
            "[ImportProgressChannel] subscribeImportProgressChannel: invalid id",
            progressId,
        );
        return;
    }

    const channelName = `import-progress.${progressId}`;

    channelRegistry.subscribe(channelName, (channel) => {
        registerImportProgressChannelListeners(channel, channelName);
    });
}

/**
 * Leave an import-progress channel (e.g. cancel before completion).
 *
 * @param {string|number} progressId
 */
export function unsubscribeImportProgressChannel(progressId) {
    if (!progressId) {
        return;
    }
    channelRegistry.unsubscribe(`import-progress.${progressId}`);
}
